import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
} from '@mui/icons-material';
import { ChangeTrackingService } from '../../services/changeTrackingService';
import { Change, ChangeStatus, ChangeEntityType, ChangeType } from '../../types';
import { ChangeCard } from '../../components/changes/ChangeCard';
import { DiffViewer } from '../../components/changes/DiffViewer';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ChangeApproval - Admin page for reviewing and approving pending changes
 *
 * Features:
 * - List of pending changes
 * - Filter by entity type, change type, user
 * - Approve/reject with notes
 * - View detailed diff
 * - Statistics dashboard
 */

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`change-tabpanel-${index}`}
      aria-labelledby={`change-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const ChangeApproval: React.FC = () => {
  const { authState } = useAuth();
  const user = authState.user;

  // ===== State =====
  const [changes, setChanges] = useState<Change[]>([]);
  const [filteredChanges, setFilteredChanges] = useState<Change[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedChange, setSelectedChange] = useState<Change | null>(null);
  const [diffViewerOpen, setDiffViewerOpen] = useState(false);

  // Filter state
  const [tabValue, setTabValue] = useState(0); // 0=Pending, 1=Approved, 2=Rejected
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all');
  const [changeTypeFilter, setChangeTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Statistics
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  // ===== Effects =====

  useEffect(() => {
    loadChanges();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [changes, tabValue, entityTypeFilter, changeTypeFilter, searchQuery]);

  useEffect(() => {
    calculateStats();
  }, [changes]);

  // ===== Data Loading =====

  const loadChanges = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all changes (in production, this should be paginated)
      const allChanges = await ChangeTrackingService.getFilteredChanges({}, 500);
      setChanges(allChanges);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    setStats({
      pending: changes.filter(c => c.status === 'pending').length,
      approved: changes.filter(c => c.status === 'approved' || c.status === 'auto-approved').length,
      rejected: changes.filter(c => c.status === 'rejected').length,
    });
  };

  const applyFilters = () => {
    let filtered = [...changes];

    // Filter by tab (status)
    const statusMap: ChangeStatus[] = ['pending', 'approved', 'rejected'];
    const status = statusMap[tabValue];
    if (status === 'approved') {
      filtered = filtered.filter(c => c.status === 'approved' || c.status === 'auto-approved');
    } else {
      filtered = filtered.filter(c => c.status === status);
    }

    // Filter by entity type
    if (entityTypeFilter !== 'all') {
      filtered = filtered.filter(c => c.entityType === entityTypeFilter);
    }

    // Filter by change type
    if (changeTypeFilter !== 'all') {
      filtered = filtered.filter(c => c.changeType === changeTypeFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.userName.toLowerCase().includes(query) ||
        c.entityId.toLowerCase().includes(query) ||
        (c.reason && c.reason.toLowerCase().includes(query))
      );
    }

    setFilteredChanges(filtered);
  };

  // ===== Event Handlers =====

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleApprove = async (changeId: string) => {
    if (!user) return;

    try {
      setError(null);
      setSuccess(null);

      await ChangeTrackingService.approveChange(
        changeId,
        user.uid,
        user.displayName || 'Admin',
        'Approved from change approval dashboard'
      );

      setSuccess('Change approved successfully');
      await loadChanges();
    } catch (err: any) {
      setError(`Failed to approve change: ${err.message}`);
    }
  };

  const handleReject = async (changeId: string, reason?: string) => {
    if (!user) return;

    const rejectionReason = reason || prompt('Please provide a reason for rejection:');
    if (!rejectionReason) return;

    try {
      setError(null);
      setSuccess(null);

      await ChangeTrackingService.rejectChange(
        changeId,
        user.uid,
        user.displayName || 'Admin',
        rejectionReason
      );

      setSuccess('Change rejected successfully');
      await loadChanges();
    } catch (err: any) {
      setError(`Failed to reject change: ${err.message}`);
    }
  };

  const handleViewDetails = (change: Change) => {
    setSelectedChange(change);
    setDiffViewerOpen(true);
  };

  const handleRefresh = () => {
    loadChanges();
  };

  // ===== Render =====

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Change Approval
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <PendingIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{stats.pending}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Changes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ApprovedIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{stats.approved}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Approved Changes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <RejectedIcon color="error" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{stats.rejected}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rejected Changes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2}} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <Paper sx={{ p: 3 }}>
        {/* Tabs */}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label={`Pending (${stats.pending})`} />
          <Tab label={`Approved (${stats.approved})`} />
          <Tab label={`Rejected (${stats.rejected})`} />
        </Tabs>

        {/* Filters */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by user, entity ID, or reason..."
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={entityTypeFilter}
                onChange={(e) => setEntityTypeFilter(e.target.value)}
                label="Entity Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="person">Person</MenuItem>
                <MenuItem value="relationship">Relationship</MenuItem>
                <MenuItem value="photo">Photo</MenuItem>
                <MenuItem value="comment">Comment</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Change Type</InputLabel>
              <Select
                value={changeTypeFilter}
                onChange={(e) => setChangeTypeFilter(e.target.value)}
                label="Change Type"
              >
                <MenuItem value="all">All Changes</MenuItem>
                <MenuItem value="create">Create</MenuItem>
                <MenuItem value="update">Update</MenuItem>
                <MenuItem value="delete">Delete</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Changes List */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : filteredChanges.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No changes found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tabValue === 0 && 'There are no pending changes at this time.'}
              {tabValue === 1 && 'No approved changes match your filters.'}
              {tabValue === 2 && 'No rejected changes match your filters.'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {filteredChanges.map((change) => (
              <ChangeCard
                key={change.id}
                change={change}
                onApprove={handleApprove}
                onReject={handleReject}
                onViewDetails={handleViewDetails}
                showActions={tabValue === 0}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Diff Viewer Dialog */}
      {selectedChange && (
        <DiffViewer
          change={selectedChange}
          open={diffViewerOpen}
          onClose={() => setDiffViewerOpen(false)}
          onApprove={tabValue === 0 ? handleApprove : undefined}
          onReject={tabValue === 0 ? (id, reason) => handleReject(id, reason) : undefined}
          showActions={tabValue === 0}
        />
      )}
    </Container>
  );
};
